import { useMemo, useState } from 'react'
import { RivalsLens } from '@/components/lenses/RivalsLens'
import { SpacesLens } from '@/components/lenses/SpacesLens'
import { rivalGaps } from '@/lib/clusters'
import type { Run } from '@/lib/types'

type Lens = 'spaces' | 'rivals'

// Provenance caption: grounded = the brand's real catalog/knowledge; raw =
// inferred from the model. Tells the user how far to trust the read.
function ProvenanceNote({ source }: { source?: string }) {
  const grounded = source === 'vwam'

  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span
        className={`size-1.5 rounded-full ${grounded ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`}
      />
      {grounded ? 'Grounded in your catalog' : 'Inferred from public data'}
    </span>
  )
}

export function GapFinder({ run }: { run: Run }) {
  const [lens, setLens] = useState<Lens>('spaces')

  // The Spaces tab is labelled with the brand ("how visible is {brand}"); Rivals
  // is who else shows up. Framing was decomposed into a strip on each tab.
  const tabs = useMemo(
    () =>
      [
        { id: 'spaces', label: run.brand, count: run.clusters?.length ?? 0 },
        {
          id: 'rivals',
          label: 'Rivals',
          count: rivalGaps(run.clusters ?? [], run.perQuery).length,
        },
      ] as const,
    [run.brand, run.clusters, run.perQuery]
  )

  return (
    <div>
      {/* Tab bar — segmented, fused to the panel below. The active tab shares the
          panel's surface + drops its bottom border so the two read as one object. */}
      <div role="tablist" aria-label="Gap Finder lenses" className="flex gap-1">
        {tabs.map((t) => {
          const isActive = t.id === lens

          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setLens(t.id)}
              className={`relative flex max-w-[16rem] cursor-pointer items-center gap-2 rounded-t-lg border border-b-0 px-4 py-2.5 text-sm font-medium transition ${
                isActive
                  ? 'z-10 -mb-px border-border bg-card text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              }`}
            >
              <span className="truncate">{t.label}</span>

              <span
                className={`rounded-full px-1.5 py-0.5 text-[11px] tabular-nums ${
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {t.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Panel — the active tab overlaps its top border (−mb-px above) so they
          read as one continuous surface. */}
      <div
        role="tabpanel"
        className="rounded-b-xl rounded-tr-xl border border-border bg-card p-6"
      >
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="text-base font-semibold">
            {lens === 'spaces'
              ? 'Where are your visibility gaps?'
              : 'Who wins these intents?'}
          </h2>
          <ProvenanceNote source={run.knowledgeSource} />
        </div>

        {lens === 'spaces' && <SpacesLens run={run} />}
        {lens === 'rivals' && <RivalsLens run={run} />}
      </div>
    </div>
  )
}
